// ============================================================
// TATHYA DMS - My Databases List
// Version: 1.1.0
// Date: 2026-04-20
// Change (v1.1): Excel import — upload .xlsx/.xls to auto-create
//         a new database. Row 1 = headers, type inference applied.
//         Import button sits next to "+ New Database".
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabases, renameDatabase, transferDatabaseOwnership, getUserDirectory, deleteDatabase } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const DatabaseList = () => {
  const { org_slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  // ── Rename state (owner/admin only, triggered from card menu) ──
  // menuOpenFor: db.id whose 3-dot menu is open (click-away closes)
  // renameTarget: db being renamed (shows inline input on the card)
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  // ── Transfer-ownership state (super_admin only) ──
  // transferTarget: the db currently being transferred (null = no modal)
  // transferUsers: candidates from /users/directory, filtered to valid
  //   new owners — active, same org, not super_admin/platform_admin,
  //   not the current owner.
  // transferSelected: user_id picked in the dropdown
  // transferSaving / transferError: mirrors the rename pattern
  // (Apr 24 — Items 9+10.)
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferUsers, setTransferUsers] = useState([]);
  const [transferSelected, setTransferSelected] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferError, setTransferError] = useState('');

  // ── Delete-database state (owner/admin only) ──
  // deleteTarget: db pending deletion (null = no modal).
  // Soft-delete on the backend (is_active = false) — card disappears
  // from the list on success. Recovery requires DB-level action.
  // (Apr 24 — card-menu delete.)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const isAdmin = ['platform_admin', 'super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const canRename = (db) => isAdmin || Number(db.owner_id) === Number(user?.id);
  const canTransferOwner = () => isSuperAdmin;

  // Close the 3-dot menu when clicking anywhere else
  useEffect(() => {
    if (!menuOpenFor) return;
    const onDown = (e) => {
      if (!e.target.closest?.('[data-card-menu]')) setMenuOpenFor(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpenFor]);

  const openRename = (db, e) => {
    e.stopPropagation();
    setMenuOpenFor(null);
    setRenameTarget(db);
    setRenameValue(db.name);
    setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);
  };

  const cancelRename = () => {
    setRenameTarget(null);
    setRenameValue('');
  };

  const commitRename = async () => {
    if (!renameTarget) return;
    const next = renameValue.trim();
    if (!next || next === renameTarget.name) {
      cancelRename();
      return;
    }
    setRenameSaving(true);
    try {
      const res = await renameDatabase(org_slug, renameTarget.id, next);
      setDatabases(prev => prev.map(d => d.id === renameTarget.id ? res.data : d));
      cancelRename();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to rename database');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleRenameKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
  };

  // ── Transfer ownership handlers ──
  // Fetch the user directory lazily (on menu click) so the list
  // always reflects current users. Filter out: super_admin,
  // platform_admin (they have full access without ownership),
  // inactive users, and the current owner of this DB (no-op).
  const openTransfer = async (db, e) => {
    e.stopPropagation();
    setMenuOpenFor(null);
    setTransferError('');
    setTransferSelected('');
    setTransferTarget(db);
    try {
      const res = await getUserDirectory(org_slug);
      const candidates = (res.data || []).filter(u =>
        u.role !== 'super_admin' &&
        u.role !== 'platform_admin' &&
        Number(u.id) !== Number(db.owner_id)
      );
      setTransferUsers(candidates);
    } catch (err) {
      setTransferError(err.response?.data?.error || 'Failed to load users');
      setTransferUsers([]);
    }
  };

  const cancelTransfer = () => {
    if (transferSaving) return;
    setTransferTarget(null);
    setTransferUsers([]);
    setTransferSelected('');
    setTransferError('');
  };

  const confirmTransfer = async () => {
    if (!transferTarget || !transferSelected) return;
    setTransferSaving(true);
    setTransferError('');
    try {
      const res = await transferDatabaseOwnership(
        org_slug, transferTarget.id, parseInt(transferSelected, 10)
      );
      // Replace the card with the fresh DB record (new owner, etc.)
      setDatabases(prev => prev.map(d => d.id === transferTarget.id ? res.data : d));
      setTransferTarget(null);
      setTransferUsers([]);
      setTransferSelected('');
    } catch (err) {
      setTransferError(err.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setTransferSaving(false);
    }
  };

  // ── Delete handlers ──
  const openDelete = (db, e) => {
    e.stopPropagation();
    setMenuOpenFor(null);
    setDeleteError('');
    setDeleteTarget(db);
  };

  const cancelDelete = () => {
    if (deleteSaving) return;
    setDeleteTarget(null);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    setDeleteError('');
    try {
      await deleteDatabase(org_slug, deleteTarget.id);
      // Optimistic: drop the card from the list. Backend soft-deletes,
      // so the next refresh wouldn't return it anyway.
      setDatabases(prev => prev.filter(d => d.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete database');
    } finally {
      setDeleteSaving(false);
    }
  };

  useEffect(() => {
    getDatabases(org_slug)
      .then(res => setDatabases(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [org_slug]);

  const filtered = databases.filter(db => {
    const q = search.toLowerCase();
    return db.name.toLowerCase().includes(q) ||
      db.db_number.toLowerCase().includes(q) ||
      (db.owner?.name || '').toLowerCase().includes(q) ||
      (db.owner?.email || '').toLowerCase().includes(q);
  });

  const handleImportClick = () => {
    setImportError('');
    setImportSummary(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected if needed
    e.target.value = '';

    setImporting(true);
    setImportError('');
    setImportSummary(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('accessToken');
      const res = await axios.post(
        `/api/${org_slug}/databases/import`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );

      const { database, summary } = res.data;
      setImportSummary(summary);

      // Brief pause so user sees the success summary, then navigate
      setTimeout(() => {
        navigate(`/${org_slug}/databases/${database.id}`);
      }, 1500);

    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Import failed';
      setImportError(msg);
      setImporting(false);
    }
  };

  return (
    <div>
      {/* Hidden file input — triggered by the Import button */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>My Databases</h1>
          <p style={styles.subtitle}>
            {databases.length} database{databases.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={styles.headerButtons}>
          <button
            className="btn btn-secondary"
            onClick={handleImportClick}
            disabled={importing}
            style={styles.importBtn}
          >
            {importing ? '⏳ Importing…' : '⬆ Import Excel'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/${org_slug}/databases/new`)}
          >
            + New Database
          </button>
        </div>
      </div>

      {/* Import feedback */}
      {importError && (
        <div style={styles.errorBanner}>
          ⚠ {importError}
        </div>
      )}
      {importSummary && (
        <div style={styles.successBanner}>
          ✓ Imported {importSummary.rows} rows across {importSummary.columns} columns.
          Opening database…
        </div>
      )}

      <input
        type="text"
        placeholder="🔍  Search databases..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={styles.search}
      />

      {loading ? (
        <div style={styles.center}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.empty}>
          {search ? 'No databases match your search.' : 'No databases yet.'}
          <button
            className="btn btn-primary"
            onClick={() => navigate(`/${org_slug}/databases/new`)}
            style={{ marginTop: '16px', display: 'block', margin: '16px auto 0' }}
          >
            Create your first database
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {filtered.map(db => {
            const isRenaming = renameTarget?.id === db.id;
            return (
            <div
              key={db.id}
              style={{ ...styles.card, position: 'relative' }}
              onClick={() => !isRenaming && navigate(`/${org_slug}/databases/${db.id}`)}
            >
              {/* 3-dot menu (owner/admin only) */}
              {canRename(db) && !isRenaming && (
                <div data-card-menu style={styles.cardMenuWrap}>
                  <button
                    style={styles.cardMenuBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenFor(menuOpenFor === db.id ? null : db.id);
                    }}
                    title="More actions"
                  >⋯</button>
                  {menuOpenFor === db.id && (
                    <div style={styles.cardMenuPopup}>
                      <button
                        style={styles.cardMenuItem}
                        onClick={(e) => openRename(db, e)}
                      >Rename</button>
                      {canTransferOwner() && (
                        <button
                          style={styles.cardMenuItem}
                          onClick={(e) => openTransfer(db, e)}
                        >Transfer Owner</button>
                      )}
                      {isAdmin && (
                        <button
                          style={{ ...styles.cardMenuItem, color: '#C0392B' }}
                          onClick={(e) => openDelete(db, e)}
                        >Delete Database</button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={styles.cardTop}>
                <span style={styles.dbNumber}>{db.db_number}</span>
                <span style={styles.colCount}>
                  {db.DbColumns?.length || 0} cols
                </span>
              </div>

              {isRenaming ? (
                <div
                  style={styles.renameWrap}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    ref={renameInputRef}
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={handleRenameKey}
                    disabled={renameSaving}
                    maxLength={255}
                    style={styles.renameInput}
                  />
                  <div style={styles.renameButtons}>
                    <button
                      style={styles.renameSave}
                      onClick={commitRename}
                      disabled={renameSaving}
                    >{renameSaving ? '…' : 'Save'}</button>
                    <button
                      style={styles.renameCancel}
                      onClick={cancelRename}
                      disabled={renameSaving}
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={styles.dbName}>{db.name}</div>
              )}

              {db.owner && (
                <div style={styles.ownerRow}>
                  <span style={styles.ownerIcon}>👤</span>
                  <span style={styles.ownerName}>{db.owner.name}</span>
                  <span style={styles.ownerEmail}>{db.owner.email}</span>
                </div>
              )}
              <div style={styles.cardBottom}>
                <span style={styles.date}>
                  {db.created_at ? new Date(db.created_at).toLocaleDateString("en-GB") : ""}
                </span>
                <span style={{
                  ...styles.status,
                  background: db.is_active ? '#E2F0D9' : '#FCE4D6',
                  color: db.is_active ? '#1E7B34' : '#C0392B'
                }}>
                  {db.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* ── Transfer-ownership modal (Items 9+10) ──
          Rendered only when transferTarget is set. Super_admin
          picks a user from the filtered directory, sees the
          confirmation line, and clicks Confirm. */}
      {transferTarget && (
        <div style={styles.modalOverlay} onClick={cancelTransfer}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Transfer Database Ownership</h3>
            <p style={styles.modalMeta}>
              <strong>{transferTarget.name}</strong>
              <span style={{ color: '#aaa', margin: '0 6px' }}>·</span>
              <span style={{ color: '#888', fontSize: 12 }}>{transferTarget.db_number}</span>
            </p>
            {transferTarget.owner && (
              <p style={styles.modalCurrentOwner}>
                Current owner: <strong>{transferTarget.owner.name}</strong>
                <span style={{ color: '#888' }}> ({transferTarget.owner.email})</span>
              </p>
            )}

            <label style={styles.modalLabel}>Transfer to:</label>
            <select
              value={transferSelected}
              onChange={(e) => setTransferSelected(e.target.value)}
              disabled={transferSaving}
              style={styles.modalSelect}
            >
              <option value="">— Select a user —</option>
              {transferUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>

            {transferUsers.length === 0 && !transferError && (
              <p style={styles.modalHint}>
                No eligible users in this organisation. Ownership can only
                be transferred to active non-admin users.
              </p>
            )}

            {/* Confirmation line — only shown once a user is picked. */}
            {transferSelected && (() => {
              const picked = transferUsers.find(u => Number(u.id) === Number(transferSelected));
              if (!picked) return null;
              return (
                <p style={styles.modalConfirm}>
                  Transfer <strong>{transferTarget.name}</strong> to{' '}
                  <strong>{picked.name}</strong>?
                </p>
              );
            })()}

            {transferError && (
              <p style={styles.modalError}>{transferError}</p>
            )}

            <div style={styles.modalButtons}>
              <button
                style={styles.modalCancel}
                onClick={cancelTransfer}
                disabled={transferSaving}
              >Cancel</button>
              <button
                style={styles.modalConfirmBtn}
                onClick={confirmTransfer}
                disabled={transferSaving || !transferSelected}
              >{transferSaving ? 'Transferring…' : 'Confirm Transfer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete-database modal ──
          Shown when deleteTarget is set. Backend soft-deletes
          (is_active=false), so data + audit log are preserved and
          the DB can be recovered by an admin if needed. */}
      {deleteTarget && (
        <div style={styles.modalOverlay} onClick={cancelDelete}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Delete Database</h3>
            <p style={styles.modalMeta}>
              <strong>{deleteTarget.name}</strong>
              <span style={{ color: '#aaa', margin: '0 6px' }}>·</span>
              <span style={{ color: '#888', fontSize: 12 }}>{deleteTarget.db_number}</span>
            </p>
            <p style={styles.modalDeleteWarn}>
              Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </p>

            {deleteError && (
              <p style={styles.modalError}>{deleteError}</p>
            )}

            <div style={styles.modalButtons}>
              <button
                style={styles.modalCancel}
                onClick={cancelDelete}
                disabled={deleteSaving}
              >Cancel</button>
              <button
                style={styles.modalDeleteBtn}
                onClick={confirmDelete}
                disabled={deleteSaving}
              >{deleteSaving ? 'Deleting…' : 'Delete Database'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  subtitle: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px'
  },
  headerButtons: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center'
  },
  importBtn: {
    fontSize: '14px'
  },
  errorBanner: {
    background: '#FCE4D6',
    color: '#C0392B',
    border: '1px solid #f5c6b8',
    borderRadius: '6px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  successBanner: {
    background: '#E2F0D9',
    color: '#1E7B34',
    border: '1px solid #b8dba8',
    borderRadius: '6px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '16px'
  },
  search: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '20px'
  },
  center: {
    textAlign: 'center',
    padding: '40px',
    color: '#2E75B6'
  },
  empty: {
    textAlign: 'center',
    padding: '60px',
    color: '#595959'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px'
  },
  card: {
    background: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  dbNumber: {
    fontSize: '11px',
    color: '#2E75B6',
    fontWeight: '600',
    letterSpacing: '0.5px'
  },
  colCount: {
    fontSize: '11px',
    background: '#D6E4F0',
    color: '#1B3A5C',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '600'
  },
  dbName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1B3A5C',
    marginBottom: '12px'
  },
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  date: {
    fontSize: '12px',
    color: '#595959'
  },
  status: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '600'
  },
  ownerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    marginBottom: '10px',
    flexWrap: 'wrap'
  },
  ownerIcon: {
    fontSize: '11px'
  },
  ownerName: {
    fontSize: '12px',
    color: '#1B3A5C',
    fontWeight: '600'
  },
  ownerEmail: {
    fontSize: '11px',
    color: '#888'
  },
  cardMenuWrap: {
    position: 'absolute',
    top: '10px',
    right: '10px'
  },
  cardMenuBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    color: '#595959',
    padding: '2px 8px',
    borderRadius: '4px',
    lineHeight: '1'
  },
  cardMenuPopup: {
    position: 'absolute',
    top: '28px',
    right: 0,
    background: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
    minWidth: '200px',
    zIndex: 10,
    overflow: 'hidden'
  },
  cardMenuItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 16px',
    background: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1B3A5C',
    whiteSpace: 'nowrap',
    lineHeight: '1.2'
  },
  renameWrap: {
    marginBottom: '12px'
  },
  renameInput: {
    width: '100%',
    fontSize: '15px',
    fontWeight: '600',
    color: '#1B3A5C',
    border: '2px solid #2E75B6',
    borderRadius: '4px',
    padding: '5px 8px',
    outline: 'none',
    background: 'white',
    boxSizing: 'border-box'
  },
  renameButtons: {
    display: 'flex',
    gap: '6px',
    marginTop: '6px'
  },
  renameSave: {
    padding: '4px 12px',
    background: '#2E75B6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  renameCancel: {
    padding: '4px 12px',
    background: '#f0f0f0',
    color: '#595959',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  // ── Transfer-ownership modal (Items 9+10) ──
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000
  },
  modalBox: {
    background: 'white', borderRadius: '8px', padding: '22px 26px',
    maxWidth: '460px', width: '90%',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
  },
  modalTitle: {
    fontSize: '18px', fontWeight: '700', color: '#1B3A5C',
    marginBottom: '10px'
  },
  modalMeta: {
    fontSize: '14px', color: '#1B3A5C', marginBottom: '4px'
  },
  modalCurrentOwner: {
    fontSize: '13px', color: '#595959', marginBottom: '16px'
  },
  modalLabel: {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#1B3A5C', marginBottom: '6px', marginTop: '4px'
  },
  modalSelect: {
    width: '100%', padding: '8px 10px', fontSize: '14px',
    border: '1px solid #ddd', borderRadius: '4px',
    outline: 'none', background: 'white'
  },
  modalHint: {
    fontSize: '12px', color: '#888', marginTop: '8px',
    fontStyle: 'italic'
  },
  modalConfirm: {
    fontSize: '13px', color: '#1B3A5C', marginTop: '14px',
    padding: '10px 12px',
    background: '#FFF8E1', border: '1px solid #FFE082',
    borderRadius: '4px'
  },
  modalError: {
    fontSize: '13px', color: '#C0392B', marginTop: '10px',
    padding: '8px 10px',
    background: '#FCE4D6', border: '1px solid #f5c6b8',
    borderRadius: '4px'
  },
  modalButtons: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
    marginTop: '20px'
  },
  modalCancel: {
    padding: '8px 18px', background: '#f0f0f0', color: '#595959',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '13px'
  },
  modalConfirmBtn: {
    padding: '8px 18px', background: '#2E75B6', color: 'white',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600'
  },
  // ── Delete modal — red accent so the action reads as destructive ──
  modalDeleteWarn: {
    fontSize: '14px', color: '#C0392B', marginTop: '14px',
    padding: '12px 14px',
    background: '#FCE4D6', border: '1px solid #f5c6b8',
    borderRadius: '4px', lineHeight: '1.5'
  },
  modalDeleteBtn: {
    padding: '8px 18px', background: '#C0392B', color: 'white',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600'
  }
};

export default DatabaseList;

